import type { SpriteSpec } from "./sprites";
import type { TrackDef } from "./track";
/** Wire protocol (JSON over WebSocket). See docs/PROTOCOL.md. */
export const PROTOCOL_VERSION = 3; // 3: players join with their pilot code (the server knows their name)
export const STATE_HZ = 20;

/** Quick chat: only these preset messages exist, so there is nothing to moderate. The index is what travels on the wire. */
export const CHAT_MSGS = ["GG!", "NICE ONE!", "OOPS!", "REMATCH?", "READY?", "GO GO GO!", "THANKS", "SORRY!"];
/** Default words per language; the admin can replace them (the server sends the lists in /api/config). */
export const CHAT_DEFAULTS: { fr: string[]; en: string[] } = {
  en: CHAT_MSGS,
  fr: ["GG!", "BRAVO!", "OUPS!", "REVANCHE?", "PRET?", "ALLEZ ALLEZ!", "MERCI", "DESOLE!"],
};
export const CHAT_WORD_MAX = 12;
export const CHAT_MIN_GAP_MS = 1200;

export interface PlayerInfo {
  id: number;
  name: string;
  machine: number;
  ready: boolean;
  team?: string; // the tag of the pilot's team, shown on the podiums
  tc?: number; // livery colour of that team (index in TEAM_COLORS)
}

export interface WireCar {
  id: number;
  pos: number;
  speed: number;
  lat: number;
  jet: boolean;
  fin: number; // 0 = racing, else finish frame (>0)
}

export type ClientMsg =
  | { t: "join"; v: number; room: string; key: string; machine: number; watch?: boolean; token?: string } // watch: only ever watches (a stream view), never takes a seat
  | { t: "ready"; ready: boolean; machine?: number }
  | { t: "rematch"; on: boolean } // vote to replay the circuit just raced (no points); starts when every player voted
  | { t: "pick"; track: number } // host of a private room: the circuit of the next race (-1: back to the season order); a picked race gives no points
  | { t: "state"; f: number; pos: number; speed: number; lat: number; jet: boolean; fin: number }
  | { t: "ping"; ts: number }
  | { t: "chat"; c: number }
  | { t: "say"; text: string } // free text (lobby chat, when the admin allows it)
  | { t: "leave" };

export type ServerMsg =
  | { t: "welcome"; v: number; id: number; room: string; host: number }
  | { t: "tracks"; v: number; custom: TrackDef[]; season: number[]; seasonClass?: number; sprites?: Record<string, SpriteSpec> }
  | { t: "lobby"; players: PlayerInfo[]; host: number; inRace: boolean; round?: number; rounds?: number; track?: number; rematch?: number[]; last?: number; pick?: number; tourn?: { name: string; rounds: number } }
  | { t: "start"; seed: number; track: number; players: PlayerInfo[]; ai: number }
  | { t: "spectate"; id: number; room: string; track: number; players: PlayerInfo[]; sinceStartMs: number; round: number; rounds: number; locked?: boolean }
  | { t: "states"; at: number; cars: WireCar[] }
  | { t: "result"; order: { id: number; frame: number }[]; track?: number; standings?: { id: number; pts: number }[]; friendly?: boolean; tourn?: { name: string; round: number; rounds: number; done?: boolean; top?: { name: string; pts: number; team?: string }[] } }
  | { t: "chat"; id: number; name: string; c: number }
  | { t: "say"; id: number; name: string; text: string }
  | { t: "saynote"; why: "off" | "runs" | "muted" | "wait" | "refused" | "link" | "empty" } // answer to the sender when a free message is not sent
  | { t: "pong"; ts: number; server: number }
  /** Maintenance switched on/off by the admin (also sent when a player tries to start a race during maintenance). `msg` is already font-safe. */
  | { t: "maintenance"; on: boolean; msg: string }
  | { t: "error"; msg: string };

export function encode(m: ClientMsg | ServerMsg): string {
  return JSON.stringify(m);
}
export function decode<T = ClientMsg | ServerMsg>(s: string): T | null {
  try {
    const o = JSON.parse(s);
    return o && typeof o.t === "string" ? (o as T) : null;
  } catch {
    return null;
  }
}
